import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getClientToken, clearClientToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Calendar, FileText, Clock, LogOut, Home, CheckCircle2, Circle } from 'lucide-react';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [events, setEvents] = useState([]);
  const [project, setProject] = useState(null);
  const clientName = localStorage.getItem('clientName') || 'Client';

  useEffect(() => { fetchClientData(); }, []);

  const fetchClientData = async () => {
    const token = getClientToken();
    try {
      const [clientRes, eventsRes, projectRes] = await Promise.all([
        axios.get(`${API}/client/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/client/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/client/project`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setClient(clientRes.data);
      setEvents(eventsRes.data);
      if (projectRes.data && projectRes.data.id) setProject(projectRes.data);
    } catch (error) {
      console.error('Error fetching client data:', error);
    }
  };

  const handleLogout = () => {
    clearClientToken();
    localStorage.removeItem('clientName');
    localStorage.removeItem('clientId');
    navigate('/portal/login');
  };

  return (
    <div data-testid="client-dashboard" className="min-h-screen bg-[#F5F5F0]">
      <header className="bg-white shadow-soft sticky top-0 z-30">
        <div className="container-custom py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Eden's Dawn" className="h-10" />
            <div className="hidden md:block">
              <p className="text-sm text-[#6B7280]">Welcome back,</p>
              <p className="font-heading text-lg text-[#2C2C2C]">{clientName}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/"><Button variant="ghost" size="sm" className="text-[#4A5D23]"><Home size={18} className="mr-2" /> Main Site</Button></Link>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-[#6B7280]"><LogOut size={18} className="mr-2" /> Sign Out</Button>
          </div>
        </div>
      </header>

      <main className="container-custom py-8">
        <Routes>
          <Route path="dashboard" element={<DashboardHome events={events} project={project} clientName={clientName} />} />
          <Route path="*" element={<DashboardHome events={events} project={project} clientName={clientName} />} />
        </Routes>
      </main>
    </div>
  );
};

const DashboardHome = ({ events, project, clientName, client }) => {
  const event = events[0];
  const completed = project?.milestones?.filter(m => m.status === 'completed').length || 0;
  const total = project?.milestones?.length || 0;
  const progress = total ? Math.round((completed / total) * 100) : 0;

  // Use client's event_date for countdown (most reliable source)
  const eventDateStr = client?.event_date || event?.event_date;
  const daysLeft = eventDateStr
    ? Math.max(0, Math.ceil((new Date(eventDateStr + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div data-testid="client-dashboard-home">
      <h1 className="font-heading text-3xl text-[#2C2C2C] mb-8">Your Event Dashboard</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Project Progress */}
          {project && (
            <div data-testid="project-tracker" className="bg-white rounded-2xl shadow-soft p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="text-sm text-[#D4AF37] uppercase tracking-wider">Project Guide</span>
                  <h2 className="font-heading text-2xl text-[#2C2C2C]">{project.project_name}</h2>
                </div>
                <span className={`px-4 py-1 rounded-full text-sm font-medium ${
                  project.overall_status === 'Completed' ? 'bg-green-100 text-green-800' :
                  project.overall_status === 'In Progress' ? 'bg-amber-100 text-amber-800' :
                  project.overall_status === 'Final Review' ? 'bg-blue-100 text-blue-800' :
                  'bg-[#E8DFD0] text-[#4A5D23]'
                }`}>
                  {project.overall_status}
                </span>
              </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-[#6B7280]">{completed} of {total} milestones complete</span>
                  <span className="font-medium text-[#4A5D23]">{progress}%</span>
                </div>
                <div className="w-full h-3 bg-[#F5F5F0] rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#4A5D23] to-[#6B7F3A] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>

              {/* Milestones Timeline */}
              <div className="space-y-0">
                {(project.milestones || []).sort((a, b) => (a.order || 0) - (b.order || 0)).map((m, i) => (
                  <div key={m.id || i} className="flex gap-4">
                    {/* Timeline line + circle */}
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        m.status === 'completed' ? 'bg-[#4A5D23]' :
                        m.status === 'in-progress' ? 'bg-[#D4AF37]' : 'bg-[#E8DFD0]'
                      }`}>
                        {m.status === 'completed' ? (
                          <CheckCircle2 size={16} className="text-white" />
                        ) : m.status === 'in-progress' ? (
                          <Clock size={16} className="text-white" />
                        ) : (
                          <Circle size={16} className="text-[#6B7280]" />
                        )}
                      </div>
                      {i < (project.milestones.length - 1) && (
                        <div className={`w-0.5 h-full min-h-[40px] ${
                          m.status === 'completed' ? 'bg-[#4A5D23]' : 'bg-[#E8DFD0]'
                        }`} />
                      )}
                    </div>

                    {/* Content */}
                    <div className={`flex-1 pb-6 ${m.status === 'completed' ? '' : ''}`}>
                      <div className="flex items-center gap-3">
                        <h4 className={`font-medium ${
                          m.status === 'completed' ? 'text-[#4A5D23]' :
                          m.status === 'in-progress' ? 'text-[#2C2C2C]' : 'text-[#6B7280]'
                        }`}>{m.title}</h4>
                        {m.status === 'in-progress' && (
                          <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">Current</span>
                        )}
                      </div>
                      {m.description && <p className="text-sm text-[#6B7280] mt-1">{m.description}</p>}
                      {m.target_date && (
                        <p className="text-xs text-[#6B7280] mt-1">
                          {m.status === 'completed' ? 'Completed' : 'Target'}: {m.completed_date || m.target_date}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {project.notes && (
                <div className="mt-4 p-4 bg-[#F5F5F0] rounded-xl">
                  <p className="text-sm text-[#6B7280]">Planner's Note:</p>
                  <p className="text-[#2C2C2C] text-sm">{project.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Event Overview */}
          {event && (
            <div className="bg-white rounded-2xl shadow-soft p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className="text-sm text-[#D4AF37] uppercase tracking-wider">Your Event</span>
                  <h2 className="font-heading text-3xl text-[#2C2C2C]">{event.title}</h2>
                </div>
                <span className={`px-4 py-1 rounded-full text-sm ${
                  event.status === 'planning' ? 'bg-[#E8DFD0] text-[#4A5D23]' :
                  event.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>{event.status?.charAt(0).toUpperCase() + event.status?.slice(1)}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E8DFD0] flex items-center justify-center">
                    <Calendar className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7280]">Event Date</p>
                    <p className="font-heading text-lg text-[#2C2C2C]">{event.event_date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#E8DFD0] flex items-center justify-center">
                    <FileText className="text-[#D4AF37]" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B7280]">Venue</p>
                    <p className="font-heading text-lg text-[#2C2C2C]">{event.venue || 'TBD'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!event && !project && (
            <div className="bg-white rounded-2xl shadow-soft p-12 text-center">
              <Calendar className="w-16 h-16 text-[#E8DFD0] mx-auto mb-4" />
              <h2 className="font-heading text-2xl text-[#2C2C2C] mb-2">Welcome, {clientName}!</h2>
              <p className="text-[#6B7280]">Your event details and project progress will appear here once planning begins.</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl shadow-soft p-6">
            <h3 className="font-heading text-xl text-[#2C2C2C] mb-4">Quick Contact</h3>
            <p className="text-sm text-[#6B7280] mb-4">Have questions about your event? We're here to help!</p>
            <a href="mailto:hello@edweddingeventsandcreations.com">
              <Button className="w-full bg-[#D4AF37] hover:bg-[#C5A028] text-white rounded-full">Contact Your Planner</Button>
            </a>
          </div>

          {daysLeft !== null && (
            <div className="bg-[#4A5D23] rounded-2xl p-6 text-white">
              <h3 className="font-heading text-xl mb-4">Countdown</h3>
              <p className="text-white/70 text-sm mb-2">Days until your event:</p>
              <p className="font-heading text-5xl text-[#D4AF37]">
                {daysLeft}
              </p>
              {eventDateStr && (
                <p className="text-white/50 text-xs mt-2">{new Date(eventDateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
              )}
            </div>
          )}

          {project && (
            <div className="bg-white rounded-2xl shadow-soft p-6">
              <h3 className="font-heading text-xl text-[#2C2C2C] mb-3">Project Summary</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between"><span className="text-[#6B7280]">Status</span><span className="font-medium text-[#2C2C2C]">{project.overall_status}</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">Milestones</span><span className="font-medium text-[#4A5D23]">{completed}/{total}</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">Progress</span><span className="font-medium text-[#4A5D23]">{progress}%</span></div>
                <div className="flex justify-between"><span className="text-[#6B7280]">Last Updated</span><span className="font-medium text-[#2C2C2C]">{new Date(project.updated_at).toLocaleDateString()}</span></div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
